import React from 'react'
import {
  Box,
  Button,
  Field,
  Flex,
  Heading,
  Input,
  Message,
  Modal,
  Text,
  Textarea,
  toast,
} from '@workwhile/ui'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useShiftDetailWorkflowsContextValue } from '../../../context/ShiftDetailWorkflowsProvider'
import { useCreateCustomShiftNotificationFlowContextValue } from '../context/CreateCustomShiftNotificationFlowProvider'
import {
  createCustomShiftNotificationSchema,
  CreateCustomShiftNotificationSchema,
} from '../schema/createCustomShiftNotification'

export const CreateCustomShiftNotificationFlowMain = () => {
  const {
    createCustomShiftNotificationFlow: { isOpen, close },
  } = useShiftDetailWorkflowsContextValue()
  const {
    state: {
      mutationState: { isPending, errorMessage },
    },
    actions: { submit, reset: resetMutation },
  } = useCreateCustomShiftNotificationFlowContextValue()

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm<CreateCustomShiftNotificationSchema>({
    resolver: zodResolver(createCustomShiftNotificationSchema),
    defaultValues: {
      deliveryHour: -2,
      title: '',
      message: '',
    },
  })

  const handleClose = () => {
    reset({
      deliveryHour: -2,
      title: '',
      message: '',
    })
    resetMutation()
    close()
  }

  const onSubmit = handleSubmit((data) => {
    submit(
      {
        deliveryHour: data.deliveryHour,
        message: data.message,
        ...(data.title ? { title: data.title } : {}),
      },
      {
        onSuccess: () => {
          toast.success('Shift notification created')
          handleClose()
        },
        onError: (error) => {
          toast.error(error.message || 'Failed to create shift notification')
        },
      }
    )
  })

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      position="top"
      loading={isPending}
      showCancel
      customCta={
        <Flex>
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit}>
            Add notification
          </Button>
        </Flex>
      }
    >
      <Box
        as="form"
        onSubmit={(event) => {
          event.preventDefault()
          onSubmit()
        }}
      >
        <Heading level={3} fontWeight="400" style={{ margin: 0 }}>
          Add shift notification
        </Heading>
        <Text mt={2} fontSize={1} color="lightText">
          Workers receive this as SMS/push. Use a negative delivery hour for
          before the shift starts (e.g. -2 = 2 hours before). Allowed range: -48
          to 4.
        </Text>

        <Field
          label="Delivery hour"
          error={errors.deliveryHour?.message?.toString()}
          // @ts-expect-error - mt is not a valid prop for Field
          mt={3}
        >
          <Input
            type="number"
            {...register('deliveryHour')}
            aria-label="Delivery hour"
            placeholder="e.g. -2"
          />
        </Field>

        <Field
          label="Title (optional)"
          error={errors.title?.message?.toString()}
          // @ts-expect-error - mt is not a valid prop for Field
          mt={3}
        >
          <Input
            {...register('title')}
            aria-label="Title"
            placeholder="Up to 100 characters"
            maxLength={100}
          />
        </Field>

        <Field
          label="Message"
          error={errors.message?.message?.toString()}
          // @ts-expect-error - mt is not a valid prop for Field
          mt={3}
        >
          <Textarea
            {...register('message')}
            aria-label="Message"
            placeholder="Up to 200 characters"
            maxLength={200}
            rows={4}
          />
        </Field>

        {errorMessage ? (
          <Message variant="error" mt={3} borderRadius="small">
            {errorMessage}
          </Message>
        ) : null}
      </Box>
    </Modal>
  )
}
